import React, { useState } from 'react';

import { Box, Collapse, Grid, Typography, useTheme } from '@mui/material';
import Popover from '@mui/material/Popover';

import { Maybe } from '@/types';
import {
	AccordionsAccordionsItem,
	ContentTypeRichText,
	NamedAttribute,
	Tab,
	Tabs,
	TabsTabsCollection,
} from '@/graphql/__generated/sdk';
import TAGButton from '@/components/TAGButton';
import TAGSvgIcon from '@/components/TAGSvgIcon';
import RichText, { RichTextContent } from '@/components/RichText';

import { servicesGroupListStyles, servicesGroupTitleStyles, serviceToggleStyles } from '../LocationCard.styles';
import { mustExcludeItem } from '../LocationCardHelpers/LocationCard.helpers';
export interface ILocationCardServiceItem {
	title?: Maybe<string>;
	details?: Maybe<AccordionsAccordionsItem>;
}
export interface ILocationCardServiceGroupProps {
	groupContent?: Maybe<Tab>;
	locationAttributes?: Maybe<NamedAttribute[]>;
}

export interface ILocationCardServices {
	availableServicesTabs: Tabs;
	locationAttributes?: Maybe<NamedAttribute[]>;
}

export function LocationServicePopover({
	children,
	details,
}: Readonly<{
	children?: Maybe<React.ReactNode>;
	details?: Maybe<AccordionsAccordionsItem>;
}>) {
	const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

	if (details?.__typename !== 'ContentTypeRichText') return <></>;

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const open = Boolean(anchorEl);
	const id = open ? 'location-service-description-open' : 'location-service-description-closed';

	return (
		<>
			{children && (
				<TAGButton
					aria-describedby={id}
					variant="text"
					onClick={handleClick}
					sx={{ padding: 0, minWidth: 0, marginLeft: '0.25rem' }}
				>
					{children}
				</TAGButton>
			)}
			<Popover
				id={id}
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
			>
				<Box sx={{ padding: '.5rem', width: '20rem' }}>
					<RichText content={details.content?.json as RichTextContent} />
				</Box>
			</Popover>
		</>
	);
}

export function LocationCardServiceGroup({
	groupContent,
	locationAttributes,
}: Readonly<ILocationCardServiceGroupProps>) {
	const theme = useTheme();
	const groupTitle = groupContent?.label;
	const groupItems = groupContent?.content?.accordionsCollection?.items;

	const [isServicesGroupVisible, setIsServicesGroupVisible] = useState<boolean>(false);

	const onToggleGroupVisibility = () => {
		return setIsServicesGroupVisible(!isServicesGroupVisible);
	};

	return (
		<Box data-test-id={`${groupTitle ?? ''}`} paddingBottom={isServicesGroupVisible ? '2rem' : '0'}>
			<Box sx={servicesGroupTitleStyles(theme)}>
				<TAGButton
					dataTestId="location_service_group_toggle"
					variant="tertiaryDefault"
					size="S"
					fullWidth={true}
					sx={serviceToggleStyles}
					onClick={onToggleGroupVisibility}
				>
					<Typography color={'text.primary'} variant="bodyMediumBold">
						{groupTitle}
					</Typography>
					<TAGSvgIcon
						icon={isServicesGroupVisible ? 'RemoveCircleOutlineOutlined' : 'AddCircleOutlineOutlined'}
						size={24}
					/>
				</TAGButton>
			</Box>
			<Collapse in={isServicesGroupVisible}>
				<Grid sx={servicesGroupListStyles}>
					{groupItems?.map((item, key) => {
						const { title } = item as ContentTypeRichText;
						const gridItemKey = `${key}`;
						if (mustExcludeItem(title, groupTitle, locationAttributes)) return null;
						return (
							<Grid item key={`location-service-item-${gridItemKey}`}>
								<Typography variant="bodySmallBook">{title}</Typography>
								{!!item && (
									<LocationServicePopover details={item}>
										<TAGSvgIcon icon={'CircleInfo'} size={16} />
									</LocationServicePopover>
								)}
							</Grid>
						);
					})}
				</Grid>
			</Collapse>
		</Box>
	);
}

export default function LocationCardServices({
	availableServicesTabs,
	locationAttributes,
}: Readonly<ILocationCardServices>) {
	const { tabsCollection } = availableServicesTabs;
	const { items } = tabsCollection as TabsTabsCollection;

	return items ? (
		<>
			{items.map((item, key) => {
				const keyName = `location-card-service-${key}`;
				return (
					<LocationCardServiceGroup
						key={keyName}
						groupContent={item}
						locationAttributes={locationAttributes}
					/>
				);
			})}
		</>
	) : null;
}
